import Player from "@vimeo/player";
import YouTubePlayer from "youtube-player"

interface PlayerInterface {
  play: () => void;
  pause: () => void;
  currentTime?: number;
  setTime(time: number): void;
}

export default function initPopupVideo() {
  const videoBlocks = document.querySelectorAll(".video-popup-block");

  videoBlocks.forEach((videoBlock) => {
    const videoPopupContainer = videoBlock.querySelector(
      ".video-popup-container"
    );

    const video = videoBlock.querySelector<HTMLElement>(".video");

    if (!video || !videoPopupContainer) {
      return;
    }

    document.body.append(videoPopupContainer);

    let player: PlayerInterface | null = null;
    let playerReady = false;

    const ensurePlayer = (): PlayerInterface | null => {
      if (playerReady) {
        return player;
      }

      const playerElement = video.querySelector<HTMLElement>(".video-player");
      if (!playerElement) {
        playerReady = true;
        return null;
      }

      const type = playerElement.dataset.videoType ?? "none";
      switch (type) {
        case "YouTube":
          player = handleYoutubePlayer(playerElement);
          break;
        case "Vimeo":
          player = handleVimeoPlayer(playerElement);
          break;
        default:
          player = handleHtml5Player(playerElement);
          break;
      }

      playerReady = true;
      return player;
    };

    const closePopup = () => {
      videoPopupContainer.classList.remove("active");
      document.body.style.overflow = "";

      const activePlayer = ensurePlayer();
      if (!activePlayer) {
        return;
      }

      activePlayer.pause();

      if (video.dataset.json !== undefined) {
        activePlayer.setTime(0);
      } else if (video.dataset.video !== undefined) {
        activePlayer.currentTime = 0;
      }
    };

    videoBlock.addEventListener("click", () => {
      videoPopupContainer.classList.add("active");
      document.body.style.overflow = "hidden";

      const activePlayer = ensurePlayer();
      if (!activePlayer) {
        return;
      }

      activePlayer.setTime(0);
      activePlayer.play();
    });

    videoPopupContainer.addEventListener("click", (event) => {
      if (event.target === videoPopupContainer) {
        closePopup();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && videoPopupContainer.classList.contains("active")) {
        closePopup();
      }
    });

    function handleVimeoPlayer(element: HTMLElement): PlayerInterface {
      const options: Record<string, string | number | boolean> = {
        autopause: element.dataset.videoAutopause === "true",
      };

      if (element.dataset.videoUrl) {
        options.url = element.dataset.videoUrl;
      } else if (element.dataset.videoId) {
        options.id = element.dataset.videoId;
      }

      if (element.dataset.videoWidth) {
        options.width = Number(element.dataset.videoWidth);
      }

      const vimeoPlayer = new Player(element, options);
      return {
        pause: () => {
          void vimeoPlayer.pause();
        },
        setTime: (time) => {
          void vimeoPlayer.setCurrentTime(time);
        },
        play: () => {
          void vimeoPlayer.play();
        },
      };
    }

    function handleYoutubePlayer(element: HTMLElement): PlayerInterface | null {
      const id = element.dataset.videoId ?? null;
      if (!id) {
        return null;
      }
      const youtubePlayer = YouTubePlayer(element, {
        videoId: id,
      });
      return {
        pause: () => {
          void youtubePlayer.pauseVideo();
        },
        setTime: (time) => {
          void youtubePlayer.seekTo(time, true);
        },
        play: () => {
          void youtubePlayer.playVideo();
        },
      };
    }

    function handleHtml5Player(element: HTMLVideoElement | HTMLElement): PlayerInterface | null {
      if (!(element instanceof HTMLVideoElement)) {
        return null;
      }
      return {
        pause: () => element.pause(),
        setTime: (time) => {
          element.currentTime = time;
        },
        play: () => {
          void element.play();
        },
      };
    }
  });
}
